/**
 * Which deployment the running bundle is pointed at.
 *
 * Walkthrough item C5, 2026-08-24: the only signal that you are on the test
 * server is the URL, which is easy to miss with several tabs open on the same
 * app. The client asked for it to be obvious in the chrome instead. InvTrac got
 * this in its PR #61; CaseTrac's half of the same item was missed.
 *
 * Driven by REACT_APP_ENVIRONMENT so a bundle is built for a target rather than
 * sniffing hostnames at runtime, which breaks the moment a custom domain or a
 * slot swap is introduced.
 */
export type DeploymentEnvironment = 'production' | 'test' | 'development';

const KNOWN: readonly DeploymentEnvironment[] = ['production', 'test', 'development'];

/** Exported for tests, which need to exercise values the bundle cannot supply. */
export function parseDeploymentEnvironment(raw: string | undefined): DeploymentEnvironment {
  const value = raw?.trim().toLowerCase();
  if (value === undefined || value === '') {
    // Unset means production. An unlabelled bundle must never imply "test",
    // because a badge that says TEST on the real server teaches people to ignore
    // it — the failure has to be in the safe direction.
    return 'production';
  }
  const match = KNOWN.find((known) => known === value);
  return match ?? 'production';
}

export function getDeploymentEnvironment(): DeploymentEnvironment {
  return parseDeploymentEnvironment(process.env.REACT_APP_ENVIRONMENT);
}

/**
 * Short label shown beside the product name, or null in production where any
 * badge would just be noise.
 */
export function getEnvironmentLabel(
  env: DeploymentEnvironment = getDeploymentEnvironment()
): string | null {
  switch (env) {
    case 'test':
      return 'TEST';
    case 'development':
      return 'DEV';
    case 'production':
    default:
      return null;
  }
}

/**
 * Browser tab title for a deployment.
 *
 * The badge beside the product name only helps once you are already looking at
 * the page. The complaint behind C5 was several tabs open on the same app, and
 * the tab strip shows the title, not the chrome. Production stays bare so an
 * unlabelled title never implies a non-production environment.
 */
export function getDocumentTitle(
  env: DeploymentEnvironment = getDeploymentEnvironment()
): string {
  const label = getEnvironmentLabel(env);
  return label === null ? 'CaseTrac' : `CaseTrac (${label})`;
}
